/**
 * Per-item streaming store for Phase 2 incremental rendering.
 *
 * The backend emits a sequence of typed SSE frames as items
 * materialise inside the InsightBuilder graph (Phase 1):
 *
 *   block_started     { block_id, type, total_expected? }
 *   kpi_card_ready    { block_id, position, card }
 *   chart_ready       { block_id, position, chart_spec }
 *   chart_dropped     { block_id, position, plan, reason }
 *   lens_item_ready   { block_id, bucket, position, item }
 *   insight_finding   { block_id, lane, finding }
 *   narrative_ready   { text }
 *   block_complete    { block_id, type, item_count }
 *
 * This module accumulates them into a mutable store, then serialises
 * the store as a synthetic v2 structured envelope (the same shape the
 * backend's ``_insight_to_structured`` produces on ``final``) so the
 * existing ``adaptStructuredPayload`` + ``transformDashboardResponse``
 * chain renders the in-flight state with zero new component code.
 *
 * Order discipline: frames carry ``position`` indices; we sort by
 * position when serialising so the render is stable even if frames
 * arrive out of order. Status is recorded so the FE can show a
 * "streaming" tile (skeleton) vs. a "complete" tile.
 */

export type StreamingBlockType =
  | 'kpi_strip'
  | 'chart'
  | 'performance_lens'
  | 'insights'
  | string;

export type StreamingBlockStatus = 'streaming' | 'complete';

export type StreamingBlock = {
  block_id: string;
  type: StreamingBlockType;
  status: StreamingBlockStatus;
  total_expected?: number;
  // KPI strip — keyed by position
  cards: Map<number, Record<string, any>>;
  // Chart spec(s) under this umbrella block id — keyed by position
  charts: Map<number, Record<string, any>>;
  // Performance lens — separate maps per bucket
  strengths: Map<number, Record<string, any>>;
  watchouts: Map<number, Record<string, any>>;
  risks: Map<number, Record<string, any>>;
  // Insights findings — append order; lane is a tag the FE buckets on
  findings: Array<{ lane: string | null | undefined; finding: Record<string, any> }>;
  // Curator's per-lane analyst prose. Only set once the curator's
  // ``insights_prose_ready`` frame arrives — the FE Insights block is
  // hidden until this is populated so we never render the raw
  // seed-stitched headline mid-flight.
  insights_prose?: {
    headline: string;
    concentration_prose: string;
    variance_drivers_prose: string;
    trend_prose: string;
    cross_dim_prose: string;
  };
};

export type StreamingStore = {
  blocks: Map<string, StreamingBlock>;
  /** Insertion order from ``block_started`` so the synthetic envelope
   *  emits blocks in the same sequence the backend produced them. */
  blockOrder: string[];
  /** Set by ``narrative_ready`` — drives the headline + top-level summary. */
  narrative: string;
  /** Set by ``executive_summary_ready`` — drives the canvas top block. */
  executiveSummary: string;
  /** Set by ``follow_up_ready`` — drives the chat panel + canvas
   *  suggestion chips. Empty until the follow_up_questions node lands. */
  followUpQuestions: string[];
};

export function createEmptyStore(): StreamingStore {
  return {
    blocks: new Map(),
    blockOrder: [],
    narrative: '',
    executiveSummary: '',
    followUpQuestions: [],
  };
}

function ensureBlock(
  store: StreamingStore,
  block_id: string,
  type: StreamingBlockType,
): StreamingBlock {
  let block = store.blocks.get(block_id);
  if (block) return block;
  block = {
    block_id,
    type,
    status: 'streaming',
    cards: new Map(),
    charts: new Map(),
    strengths: new Map(),
    watchouts: new Map(),
    risks: new Map(),
    findings: [],
  };
  store.blocks.set(block_id, block);
  store.blockOrder.push(block_id);
  return block;
}

/**
 * Apply one streaming frame to the store. Mutates in place and returns
 * the same store object so callers can chain. Unknown frame types are
 * silently ignored — keeps the consumer hook resilient to backend
 * additions.
 */
export function applyFrame(
  store: StreamingStore,
  event: string,
  data: Record<string, any>,
): StreamingStore {
  switch (event) {
    case 'block_started': {
      const id = String(data.block_id || '');
      const type = String(data.type || '');
      if (!id || !type) return store;
      const b = ensureBlock(store, id, type);
      b.status = 'streaming';
      if (typeof data.total_expected === 'number') {
        b.total_expected = data.total_expected;
      }
      return store;
    }

    case 'block_complete': {
      const id = String(data.block_id || '');
      const b = store.blocks.get(id);
      if (b) b.status = 'complete';
      return store;
    }

    case 'kpi_card_ready': {
      const id = String(data.block_id || 'block.kpi_strip');
      const position = typeof data.position === 'number' ? data.position : 0;
      const card = (data.card && typeof data.card === 'object') ? data.card : null;
      if (!card) return store;
      const b = ensureBlock(store, id, 'kpi_strip');
      b.cards.set(position, card);
      return store;
    }

    case 'chart_ready': {
      const id = String(data.block_id || 'block.charts');
      const position = typeof data.position === 'number' ? data.position : 0;
      const spec = (data.chart_spec && typeof data.chart_spec === 'object')
        ? data.chart_spec : null;
      if (!spec) return store;
      const b = ensureBlock(store, id, 'chart');
      b.charts.set(position, spec);
      return store;
    }

    case 'chart_dropped':
      // No-op for render — the FE has no slot for dropped charts. The
      // payload still hits the debug ring buffer via the existing
      // ``recordStreamDebugFrame`` path.
      return store;

    case 'lens_item_ready': {
      const id = String(data.block_id || 'block.performance_lens');
      const bucket = String(data.bucket || '');
      const position = typeof data.position === 'number' ? data.position : 0;
      const item = (data.item && typeof data.item === 'object') ? data.item : null;
      if (!item || !bucket) return store;
      const b = ensureBlock(store, id, 'performance_lens');
      const target = bucket === 'strengths' ? b.strengths
        : bucket === 'watchouts' ? b.watchouts
          : bucket === 'risks' ? b.risks
            : null;
      if (!target) return store;
      target.set(position, item);
      return store;
    }

    case 'insight_finding': {
      const id = String(data.block_id || 'block.insights');
      const finding = (data.finding && typeof data.finding === 'object') ? data.finding : null;
      if (!finding) return store;
      const b = ensureBlock(store, id, 'insights');
      b.findings.push({ lane: data.lane ?? null, finding });
      return store;
    }

    case 'insights_prose_ready': {
      const id = String(data.block_id || 'block.insights');
      const b = ensureBlock(store, id, 'insights');
      b.insights_prose = {
        headline:               String(data.headline || ''),
        concentration_prose:    String(data.concentration_prose || ''),
        variance_drivers_prose: String(data.variance_drivers_prose || ''),
        trend_prose:            String(data.trend_prose || ''),
        cross_dim_prose:        String(data.cross_dim_prose || ''),
      };
      return store;
    }

    case 'narrative_ready': {
      const text = typeof data.text === 'string' ? data.text : '';
      if (text) store.narrative = text;
      return store;
    }

    case 'executive_summary_ready': {
      const text = typeof data.summary === 'string' ? data.summary.trim() : '';
      store.executiveSummary = text;
      return store;
    }

    case 'follow_up_ready': {
      const list = Array.isArray(data.questions)
        ? data.questions
            .map((q: any) => (typeof q === 'string' ? q.trim() : ''))
            .filter((q: string) => q.length > 0)
        : [];
      store.followUpQuestions = list;
      return store;
    }

    default:
      return store;
  }
}

function sortedByPosition<T>(m: Map<number, T>): T[] {
  return [...m.entries()].sort((a, b) => a[0] - b[0]).map(([, v]) => v);
}

function bucketByLane(
  findings: StreamingBlock['findings'],
): Record<string, Record<string, any>[]> {
  const lanes: Record<string, Record<string, any>[]> = {
    trend: [], concentration: [], variance_drivers: [], cross_dim: [],
  };
  for (const { lane, finding } of findings) {
    const key = (lane && typeof lane === 'string' && lanes[lane]) ? lane : 'concentration';
    lanes[key].push(finding);
  }
  return lanes;
}

/**
 * Compose a synthetic ``structured`` envelope identical in shape to
 * what the backend's ``_insight_to_structured`` emits on ``final`` —
 * lets the existing ``adaptStructuredPayload`` +
 * ``transformDashboardResponse`` rendering chain consume the in-flight
 * state with no special-casing on the consumer side.
 *
 * Block layout:
 *   * ``headline`` (always first when narrative present)
 *   * ``kpi_strip`` — items[] sorted by position
 *   * one ``chart`` block per chart_ready frame (the backend assembler
 *     does the same — each chart is its own block) so the FE renders
 *     them progressively rather than waiting for the full umbrella to
 *     arrive
 *   * ``performance_lens`` — strengths / watchouts / risks
 *   * ``insights`` — bucketed findings + an aggregated headline
 *
 * Each block carries ``status: "streaming" | "complete"`` so the
 * renderer can show skeleton tiles when ``total_expected`` exceeds
 * the items currently in the store.
 */
export function buildSyntheticEnvelope(
  store: StreamingStore,
  query: string,
): Record<string, any> {
  const blocks: Record<string, any>[] = [];
  let blockIndex = 0;
  const nextId = () => `s${blockIndex++}`;

  // Executive summary block — emit at the top of the canvas the
  // moment the executive_summary node lands. Backed by
  // ``store.executiveSummary`` (set by ``executive_summary_ready``).
  // No "Summary" tile chrome — renders as a clean top-of-canvas card.
  if (store.executiveSummary) {
    blocks.push({
      id: nextId(),
      type: 'executive_summary',
      heading: '',
      relevance: { salience: 100, rationale: '', answers_subquestion: query },
      layout: { span: 12 },
      data: { text: store.executiveSummary },
      streaming_status: 'complete',
    });
  }
  // Legacy ``headline`` block is intentionally NOT emitted — it has
  // been replaced by the executive_summary block above. The ``final``
  // envelope from the backend likewise no longer carries a headline
  // block; the assembler emits ``executive_summary`` in its place.

  // Walk in insertion order so the FE sees the same sequence the
  // backend produced (kpi_strip → charts → lens → insights typically).
  for (const block_id of store.blockOrder) {
    const b = store.blocks.get(block_id);
    if (!b) continue;
    const status = b.status;

    if (b.type === 'kpi_strip') {
      const items = sortedByPosition(b.cards);
      // Skip empty kpi_strip when streaming hasn't produced anything
      // yet — avoids flashing a 0-card grid.
      if (items.length === 0 && status !== 'complete') continue;
      blocks.push({
        id: nextId(),
        type: 'kpi_strip',
        heading: 'Key Numbers',
        relevance: { salience: 93, rationale: '', answers_subquestion: query },
        layout: { span: 12 },
        data: { items },
        streaming_status: status,
        total_expected: b.total_expected,
      });
      continue;
    }

    if (b.type === 'chart') {
      // Each chart becomes its OWN block — matches the assembler.
      const charts = sortedByPosition(b.charts);
      for (const chart of charts) {
        blocks.push({
          id: nextId(),
          type: 'chart',
          heading: chart.title || 'Chart',
          relevance: {
            salience: 88,
            rationale: chart.intent || '',
            answers_subquestion: query,
          },
          layout: { span: 12 },
          data: chart,
          streaming_status: 'complete',
        });
      }
      // While charts are still arriving, emit a skeleton block per
      // missing slot so the FE can show placeholder tiles. We size
      // the skeleton list off ``total_expected`` minus what's
      // already in the store.
      const expected = b.total_expected ?? charts.length;
      const missing = Math.max(0, expected - charts.length);
      if (status === 'streaming' && missing > 0) {
        for (let i = 0; i < missing; i += 1) {
          blocks.push({
            id: nextId(),
            type: 'chart',
            heading: 'Chart',
            relevance: { salience: 88, rationale: '', answers_subquestion: query },
            layout: { span: 12 },
            data: { chart_type: 'bar', title: 'Chart', data: [] },
            streaming_status: 'streaming',
            skeleton: true,
          });
        }
      }
      continue;
    }

    if (b.type === 'performance_lens') {
      const strengths = sortedByPosition(b.strengths);
      const watchouts = sortedByPosition(b.watchouts);
      const risks = sortedByPosition(b.risks);
      if (strengths.length + watchouts.length + risks.length === 0 && status !== 'complete') {
        continue;
      }
      blocks.push({
        id: nextId(),
        type: 'performance_lens',
        heading: 'Performance Lens',
        relevance: { salience: 89, rationale: '', answers_subquestion: query },
        layout: { span: 12 },
        data: { strengths, watchouts, risks },
        streaming_status: status,
      });
      continue;
    }

    if (b.type === 'insights') {
      // The Insights block is gated on the curator's prose frame.
      // Detector findings stream in via ``insight_finding`` and
      // populate ``b.findings``, but we DON'T render the block from
      // those alone — that would surface the raw seed-stitched
      // headline (technical column names) and rerender when the
      // curator finishes. Wait for ``insights_prose_ready`` instead
      // and render the full lane-titled CFO prose in one shot.
      if (!b.insights_prose) continue;
      const lanes = bucketByLane(b.findings);
      blocks.push({
        id: nextId(),
        type: 'insights',
        heading: 'Insights',
        relevance: { salience: 84, rationale: '', answers_subquestion: query },
        layout: { span: 12 },
        data: {
          headline:               b.insights_prose.headline,
          concentration_prose:    b.insights_prose.concentration_prose,
          variance_drivers_prose: b.insights_prose.variance_drivers_prose,
          trend_prose:            b.insights_prose.trend_prose,
          cross_dim_prose:        b.insights_prose.cross_dim_prose,
          ...lanes,
        },
        streaming_status: status,
      });
      continue;
    }
  }

  return {
    $schema: 'smartchg-chat/v1',
    query,
    // ``summary`` intentionally omitted on the streaming envelope —
    // see the headline-block comment above. The authoritative
    // envelope from ``final`` carries it.
    summary: '',
    blocks,
    suggested_queries: store.followUpQuestions,
    streaming: true,
  };
}

/** Convenience — flush a frame and return the resulting envelope in one go. */
export function applyAndBuild(
  store: StreamingStore,
  event: string,
  data: Record<string, any>,
  query: string,
): { store: StreamingStore; envelope: Record<string, any> } {
  applyFrame(store, event, data);
  return { store, envelope: buildSyntheticEnvelope(store, query) };
}
